import { addDateTime } from '../../util/date';
import { sortedSizeOptions } from './sortedSizeOptions';

// requestProducts action returns size codes as 'description'
// Attempt to create continuity until new route implemented
export const reconcileDescription = styles =>
  styles.map(style => ({
    ...style,
    products: style.products.map(product => ({
      ...product,
      sizes: product.sizes.map(size => ({
        promoIndicator: size.promoIndicator,
        sizeCode: size.description || size.sizeCode,
        displaySize: size.displaySize,
        sizeConversions: size.sizeConversions,
        sortOrderNumber: size.sortOrderNumber,
        upc: size.upc
      }))
    }))
  }));

export const mergeFacets = (newFacets, existingFacets, selectedFilters, updateFilters) =>
  updateFilters && newFacets.length
    ? newFacets.map(facet => {
        let preserveExistingFacet;

        if (facet.key === 'division' || facet.key === 'gender' || facet.key in selectedFilters) {
          preserveExistingFacet = existingFacets.find(f => f.key === facet.key);
        }

        const sortedFacetValues = (preserveExistingFacet ? preserveExistingFacet.values : facet.values).sort((a, b) => {
          const maxValue = 9001;

          const aSortOrder = sortedSizeOptions[a.split('||')[0]] || maxValue;
          const bSortOrder = sortedSizeOptions[b.split('||')[0]] || maxValue;

          if (aSortOrder > bSortOrder) {
            return 1;
          }

          if (aSortOrder < bSortOrder) {
            return -1;
          }

          return 0;
        });

        return { ...facet, values: sortedFacetValues };
      })
    : existingFacets;

export const groupBySizeThenType = availabilities => {
  if (!availabilities.length) {
    return {};
  }

  return availabilities.reduce((acc, product) => {
    acc[product.productCode] = {};

    product.sizes.forEach(size => {
      if (!acc[product.productCode][size.sizeCode]) {
        acc[product.productCode][size.sizeCode] = {};
      }

      if (!acc[product.productCode][size.sizeCode][size.type]) {
        acc[product.productCode][size.sizeCode][size.type] = 0;
      }

      acc[product.productCode][size.sizeCode][size.type] += size.quantity;
    });

    return acc;
  }, {});
};

export const groupBySizeThenDateThenType = availabilities => {
  if (!availabilities.length) {
    return {};
  }

  const dateIn90Days = new Date(addDateTime({ dateTime: new Date().toISOString(), days: 90 })).getTime();

  return availabilities.reduce((acc, product) => {
    acc[product.productCode] = {};

    product.sizes.forEach(size => {
      const monthYearDate = size.startDate;
      const availableDate = new Date(monthYearDate).getTime();
      // make sure date is within 90 days
      if (availableDate < dateIn90Days) {
        if (!acc[product.productCode][size.sizeCode]) {
          acc[product.productCode][size.sizeCode] = {};
        }

        if (!acc[product.productCode][size.sizeCode][monthYearDate]) {
          acc[product.productCode][size.sizeCode][monthYearDate] = {};
        }

        if (!acc[product.productCode][size.sizeCode][monthYearDate][size.type]) {
          acc[product.productCode][size.sizeCode][monthYearDate][size.type] = 0;
        }

        acc[product.productCode][size.sizeCode][monthYearDate][size.type] += size.quantity;
      }
    });

    return acc;
  }, {});
};

export const groupProductDetailSizeAvailability = productDetails => {
  return productDetails.reduce((acc, product) => {
    acc[product.productCode] = {};

    product.sizes.forEach(size => {
      size.availabilities.forEach(avail => {
        if (!acc[product.productCode][size.description]) {
          acc[product.productCode][size.description] = {};
        }

        const availType = avail.type || 'CONTRACT'; // only GENERAL has type, CONTRACT just has contractId
        if (!acc[product.productCode][size.description][availType]) {
          acc[product.productCode][size.description][availType] = 0;
        }
        acc[product.productCode][size.description][availType] += avail.quantity;
      });
    });

    return acc;
  }, {});
};

export const groupProductDetailSizeAvailabilities = productDetails => {
  const availabilities = {};
  const futureAvailabilities = {};
  const dateIn90Days = new Date(addDateTime({ dateTime: new Date().toISOString(), days: 90 })).getTime();
  productDetails.forEach(product => {
    availabilities[product.productCode] = {};
    futureAvailabilities[product.productCode] = {};

    product.sizes.forEach(size => {
      size.availabilities.forEach(avail => {
        if (!availabilities[product.productCode][size.description]) {
          availabilities[product.productCode][size.description] = {};
        }

        const availType = avail.type || 'CONTRACT'; // only GENERAL has type, CONTRACT just has contractId
        if (!availabilities[product.productCode][size.description][availType]) {
          availabilities[product.productCode][size.description][availType] = 0;
        }
        availabilities[product.productCode][size.description][availType] += avail.quantity;
      });

      size.futureAvailabilities.forEach(futureAvail => {
        const monthYearDate = futureAvail.startDate;
        const availableDate = new Date(monthYearDate).getTime();
        // make sure date is within 90 days
        if (availableDate < dateIn90Days) {
          if (!futureAvailabilities[product.productCode][size.description]) {
            futureAvailabilities[product.productCode][size.description] = {};
          }

          if (!futureAvailabilities[product.productCode][size.description][monthYearDate]) {
            futureAvailabilities[product.productCode][size.description][monthYearDate] = {};
          }

          const availType = futureAvail.type || 'CONTRACT';
          if (!futureAvailabilities[product.productCode][size.description][monthYearDate][availType]) {
            futureAvailabilities[product.productCode][size.description][monthYearDate][availType] = 0;
          }

          futureAvailabilities[product.productCode][size.description][monthYearDate][availType] += futureAvail.quantity;
        }
      });
    });
  });
  return { availabilities, futureAvailabilities };
};

// preserve order of Available products, then follow with Available Soon then Out of Stock
const availabilityCompareFunction = (a, b) => {
  if (a.availabilityStatus === 'Available' && b.availabilityStatus === 'Available') {
    return 0;
  }
  if (a.availabilityStatus === 'Available') {
    return -1;
  }
  if (b.availabilityStatus === 'Available') {
    return 1;
  }
  if (a.availabilityStatus === 'Available Soon') {
    return -1;
  }
  if (b.availabilityStatus === 'Available Soon') {
    return 1;
  }
  return -1;
};

export const sortByAvailability = styles =>
  styles.map(style => ({ ...style, products: style.products.sort(availabilityCompareFunction) }));

export const mapAvailabilityToTranslationString = status => {
  switch (status) {
    case 'Available':
      return 'global.available';
    case 'Available Soon':
      return 'product.availableSoon';
    case 'Out of Stock':
      return 'product.outOfStock';
    default:
      return 'global.available';
  }
};
